/**
 * Glossary Stats Utilities
 * 
 * Helper functions to extract stat-related entries from the glossary.
 * These utilities enable the character sheet to be dynamically driven by
 * glossary data instead of hardcoded configurations.
 */

import { GLOSSARY } from '~/generated/glossary.data';
import type { GlossaryEntry } from '~/generated/glossary.types';

/**
 * Get all glossary entries of a specific type.
 * 
 * @param type - Entry type to filter by (e.g., "Core Stat", "Skill Check", "Derived Stat")
 * @returns Array of glossary entries matching the type
 */
export function getGlossaryEntriesByType(type: string): GlossaryEntry[] {
  return Object.values(GLOSSARY).filter(entry => entry.type === type);
}

/**
 * Get core stats from glossary (entries with type: "Core Stat").
 * 
 * In the default ruleset, this returns Mind, Body, and Reflex.
 * 
 * @returns Array of core stat glossary entries
 */
export function getCoreStats(): GlossaryEntry[] {
  return getGlossaryEntriesByType('Core Stat');
}

/**
 * Get skill checks from glossary (entries with type: "Skill Check").
 * 
 * In the default ruleset, this returns Accuracy, Acrobatics, Dodge, Knowledge,
 * Perception, Resilience Check, Stealth, and Strength.
 * 
 * @returns Array of skill check glossary entries
 */
export function getSkillChecks(): GlossaryEntry[] {
  return getGlossaryEntriesByType('Skill Check');
}

/**
 * Get derived stats from glossary (entries with type: "Derived Stat").
 * 
 * In the default ruleset, this returns Health Points, Movement, and Resilience.
 * 
 * @returns Array of derived stat glossary entries
 */
export function getDerivedStats(): GlossaryEntry[] {
  return getGlossaryEntriesByType('Derived Stat');
}
